import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Pipe,
    PipeTransform,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {CMSConditions, CMSMetaData} from '../../../../../../../shared/models';
import {FlowManagerService} from '../../../../../services/flow-manager.service';
import {
    CHANNEL_DEFAULTS,
    CUSTOM_OPTIONS,
    DISPLAY_TYPE_INDEX_LIMIT,
    DisplayTypeChannel,
    DisplayTypeModalData,
    displayTypeOptionsPerChannel,
    getSupportedDisplayChannels,
    SELECTED_DISPLAY_TYPE_TEXT,
    supportedDisplayChannels_fallback,
} from './displayTypeUtils';
import {of, Subscription} from 'rxjs';
import {DisplayTypeService} from '../../../../../services/display-type.service';
import {CMSService} from '../../../../../../../shared/components/cms/service/cms.service';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import {MatDialog} from '@angular/material/dialog';
import {catchError, map} from 'rxjs/operators';
import {maxLength, specialCharsNotAllowed} from '../../../../../../../shared/utils';

@Component({
    selector: 'display-type-btn',
    templateUrl: './display-type-dialog.component.html',
    styleUrls: ['./display-type-dialog.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DisplayTypeDialogComponent),
            multi: true
        }
    ]
})
export class DisplayTypeDialogComponent implements OnInit, OnDestroy, OnChanges {

    @Input() optionLength: number = undefined;
    @Input() data: DisplayTypeModalData = undefined;
    @Input() hasCountWarning = true;
    @Output() change = new EventEmitter<DisplayTypeModalData>();

    @ViewChild('displayTypeModal', {static: true}) displayTypeModal: TemplateRef<any>;

    @ViewChild('moreInfoSlider') moreInfoSlider: ElementRef;

    @ViewChild('customListMsgSlider') customListMsgSlider: ElementRef;
    showCustomMsgForm = false;
    selectedChannelId: number;
    selectedChannelType: string;

    private channelSubscription: Subscription;
    warningMessage = false;
    disableReset = false;
    message = '';
    displayTypeOptionsPerChannels = displayTypeOptionsPerChannel;

    constructor(
        private fb: FormBuilder,
        private flowManagerService: FlowManagerService,
        private displayService: DisplayTypeService,
        private cmsService: CMSService,
        public dialog: MatDialog) {
    }

    displayInfo: Object;
    cmsMetaData: CMSMetaData;
    cmsImageUrl = '';
    limitAndExample = '';
    rangeDisplay = '';
    exampleDisplay = '';
    readonly displayIndex = DISPLAY_TYPE_INDEX_LIMIT;

    customListButtonText = '';

    displayType: FormGroup = this.fb.group({});
    customListButtonForm: FormGroup = this.fb.group({
        'customListButton': ['', [
            specialCharsNotAllowed(),
            maxLength(20, 'Button text max length is 20')
        ]
        ]
    });

    supportedChannels: DisplayTypeChannel[];


    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this.channelSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.data) {
            this.getSupportedChannels();
        }
    }


    onOpenClick() {
        this.showCustomMsgForm = false;
        this.dialog.open(this.displayTypeModal, {
            panelClass: 'default-dialog',
            width: '874px',
            height: '572px',
        });
    }

    onCloseClick() {
        this.dialog.closeAll();
    }

    onSaveClick() {
        this.onChange();
        this.onCloseClick();
    }

    onChange: any = () => {
        this.change.emit({
            isDirty: this.displayType.dirty,
            displayType: this.displayType.getRawValue(),
            customListButton: this.customListButtonForm.getRawValue().customListButton
        });
    }

    // Todo: Should reset to default set all channels or only the one you are on
    onResetClick() {
        for (const supportedChannel of this.supportedChannels) {
            const channelId: number = supportedChannel.channelId;
            const defaultDisplayType = CHANNEL_DEFAULTS[channelId];
            this.displayType.get(`${channelId}`).setValue(defaultDisplayType);
            if (channelId === this.selectedChannelId) {
                this.selectedChannelType = defaultDisplayType;
            }
        }
        this.setWarningMessageStatus();
    }

    setSelectedChannel(channelId: number) {
        this.selectedChannelId = channelId;
        this.closeCustomMsgSlider();
        this.selectedChannelType = this.displayType.get(channelId.toString()).value.toUpperCase();
        this.displayType.markAsDirty();
        this.setWarningMessageStatus(channelId);
    }

    setWarningMessageStatus(selectedChannelId?: number) {
        this.displayService.currentIndex = this.optionLength;
        if (this.optionLength) {
            this.warningMessage = this.displayService.checkIsOptionInvalid(this.optionLength, this.selectedChannelType, selectedChannelId);
        }

        if (!this.hasCountWarning) {
            this.warningMessage = this.displayService.checkOptionsMaxLength(this.selectedChannelType, selectedChannelId) > 0;
        }

        this.message = this.displayService.buildMessage(this.hasCountWarning);
    }

    onOpenMoreInfo($event, displayType: string) {
        $event.stopPropagation();
        const cmsData: CMSConditions = {
            type: 'nodeBuilder',
            channelId: this.selectedChannelId,
            size: '17',
            field: displayType
        };

        this.cmsMetaData = this.cmsService.cmsMetaDataBuilder(cmsData);
        this.getCMSDisplayInfo();
        this.moreInfoSlider.nativeElement.style.right = 0;
    }

    onDisplayTypeSelected(displayType: string) {
        this.disableReset = false;
        this.displayType.get(this.selectedChannelId.toString()).setValue(displayType);
        this.displayType.markAsTouched();
        this.selectedChannelType = displayType.toUpperCase();
        this.setWarningMessageStatus(this.selectedChannelId);
    }

    getCMSDisplayInfo() {
        this.resetCMSData();
        if (this.cmsMetaData.contentId !== undefined) {
            const cmsData$ = this.cmsService.cmsCall(this.cmsMetaData.contentId, 'entry');
            cmsData$.toPromise().then((value) => {
                this.displayInfo = value;
                if (value['limitAndExample']) {
                    this.limitAndExample = value['limitAndExample']?.replace(/\n/g, '/');

                    const delimIndex = this.limitAndExample.indexOf('/');
                    this.rangeDisplay = this.limitAndExample.substring(0, delimIndex);
                    this.exampleDisplay = this.limitAndExample.substring(delimIndex + 1, this.limitAndExample.length);
                }

                if (value['previewImage']) {
                    this.cmsService.cmsCall(this.displayInfo['previewImage']['sys']['id'], 'asset').subscribe(
                        (data) => {
                            this.cmsImageUrl = 'https:' + data['file']['url'];
                        }
                    );
                }
            });
        }
    }

    resetCMSData() {
        this.limitAndExample = '';
        this.displayInfo = '';
        this.rangeDisplay = '';
        this.exampleDisplay = '';
        this.cmsImageUrl = '';
    }

    onCloseMoreInfoSlider() {
        this.moreInfoSlider.nativeElement.style.right = '-265px';
    }

    onModalClick(event) {
        event.stopPropagation();
        const openMoreClicked = find(event.path, item => {
            return item.className === 'more-info-container' || item.className === 'more-info-slider';
        });

        if (!openMoreClicked) {
            this.onCloseMoreInfoSlider();
        }
    }

    onOpenCustomListMessage() {
        this.customListButtonText = this.customListButtonForm.getRawValue().customListButton;
        this.customListMsgSlider.nativeElement.style.right = 0;
        this.showCustomMsgForm = true;
    }

    onSubmit() {
        this.closeCustomMsgSlider();
    }

    onCloseCustomMsgSlider() {
        this.closeCustomMsgSlider();
        this.customListButtonForm.get('customListButton').setValue(this.customListButtonText);
    }

    closeCustomMsgSlider() {
        this.customListMsgSlider.nativeElement.style.right = '-265px';
        this.showCustomMsgForm = false;
    }

    private getSupportedChannels() {
        const success = (supportedChannels: DisplayTypeChannel[]) => {
            this.supportedChannels = supportedChannels;
            this.setupForm(supportedChannels, this.data);
            if (isEmpty(this.data.displayType) && isEmpty(this.data.customListButton)) {
                this.onChange();
            }
        };

        this.channelSubscription = this.flowManagerService.getAllChannelConfig()
            .pipe(
                map(getSupportedDisplayChannels),
                catchError(() => of(supportedDisplayChannels_fallback(this.data.displayType)))
            ).subscribe(success);
    }

    private setupForm(supportedChannels: DisplayTypeChannel[], data: DisplayTypeModalData) {
        const displayType = data?.displayType ? data?.displayType : {};
        const customListButton = data.customListButton ? data.customListButton : '';
        for (const supportedChannel of supportedChannels) {

            const channelId: string = String(supportedChannel.channelId);

            if (isEmpty(displayType) || !isEmpty(displayType) && !displayType[supportedChannel.channelId]) {
                this.displayType.addControl(channelId, new FormControl(CHANNEL_DEFAULTS[supportedChannel.channelId], [Validators.required]));
                continue;
            }
            const selected = displayType[channelId];

            this.displayType.addControl(channelId, new FormControl(selected, [Validators.required]));
        }

        this.customListButtonForm.get('customListButton').setValue(customListButton);
    }
}

@Pipe({name: 'setDisplayTypeDisplayName'})
export class SetDisplayTypeDisplayName implements PipeTransform {

    transform(displayType: string, channelId: number) {
        const type = displayType.toUpperCase();
        if (CHANNEL_DEFAULTS[channelId] === type) {
            return `${DISPLAY_TYPE_INDEX_LIMIT[channelId][type].name} ${SELECTED_DISPLAY_TYPE_TEXT.default}`;
        }
        return DISPLAY_TYPE_INDEX_LIMIT[channelId][type].name;
    }
}

@Pipe({name: 'upperCaseChannelName'})
export class UpperCaseChannelName implements PipeTransform {

    transform(channelName: string) {
        if (channelName === 'sms' || channelName === 'ussd') {
            return channelName.toUpperCase();
        } else {
            return channelName;
        }
    }
}

@Pipe({name: 'customOptions'})
export class CustomOptions implements PipeTransform {

    transform(channelId: number, displayType: string): boolean {
        const options = CUSTOM_OPTIONS[channelId];
        if (isEmpty(options)) {
            return false;
        }
        return options.includes(displayType);
    }
}
